import { Directive, ElementRef, Input, OnChanges, Renderer2, SimpleChanges } from '@angular/core';

@Directive({
  selector: '[appHighlightSearch]'
})
export class HighlightSearchDirective implements OnChanges {
  @Input() appHighlightSearch: string; // The entire text to be processed
  @Input() searchTerm: string;
  @Input() highlightClass: string;
  @Input() enableMultiTokenSearch = false;

  constructor(private el: ElementRef, private renderer: Renderer2) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.appHighlightSearch || changes.searchTerm || changes.enableMultiTokenSearch) {
      this.applyHighlight();
    }
  }

  private applyHighlight(): void {
    const sourceText = this.appHighlightSearch ?? '';
    if (!this.searchTerm?.trim()) {
      this.renderer.setProperty(this.el.nativeElement, 'textContent', sourceText);
      return;
    }

    const terms = this.enableMultiTokenSearch
      ? this.getSearchTokens(this.searchTerm)
      : [this.searchTerm.trim()];

    if (terms.length === 0) {
      this.renderer.setProperty(this.el.nativeElement, 'textContent', sourceText);
      return;
    }

    const escapedTerms = terms
      .map(term => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
      .sort((a, b) => b.length - a.length);

    const regex = new RegExp(`(${escapedTerms.join('|')})`, 'gi');
    const parts = sourceText.split(regex);

    this.renderer.setProperty(this.el.nativeElement, 'textContent', '');

    parts.forEach(part => {
      const isMatch = regex.test(part);
      regex.lastIndex = 0;
      if (isMatch) {
        const span = this.renderer.createElement('span');
        this.renderer.addClass(span, this.highlightClass);
        const text = this.renderer.createText(part);
        this.renderer.appendChild(span, text);
        this.renderer.appendChild(this.el.nativeElement, span);
      } else {
        const text = this.renderer.createText(part);
        this.renderer.appendChild(this.el.nativeElement, text);
      }
    });
  }

  private getSearchTokens(searchTerm: string): string[] {
    const tokens: string[] = [];
    let currentToken = '';
    let quoteChar: '"' | "'" | null = null;

    const pushToken = () => {
      const token = currentToken.trim();
      if (token.length > 0) {
        tokens.push(token);
      }
      currentToken = '';
    };

    for (const ch of searchTerm) {
      if (quoteChar) {
        if (ch === quoteChar) {
          pushToken();
          quoteChar = null;
        } else {
          currentToken += ch;
        }
        continue;
      }

      if (ch === '"' || ch === "'") {
        pushToken();
        quoteChar = ch;
        continue;
      }

      if (/\s|,|;/.test(ch)) {
        pushToken();
        continue;
      }

      currentToken += ch;
    }

    pushToken();

    return Array.from(new Set(tokens));
  }
}
